import { mountMatchPanel, type PanelContract } from '../../match-panel';
import { mountDateFilter, type DateFilterConfig } from '../../../../shared/date-filter';
import type { LayoutRuntime } from "../../../../shared/layouts/registry";
import { renderCustomOdds } from '../../custom-template';
import { mountBetPopup, prepareBetTriggers, syncBetPopup } from '../../bet-popup';
import { renderOddsBoard, orderedOddsMatches } from "../../normalize";
import { createOddsVirtualList } from '../../virtual-list';
import { formatMatchClockHtml } from "../../../livescore/match-status";
import { normalizeDisplayOddsFormat } from "../../../../shared/odds-format";
import { livescoreRuntime } from "../../../../shared/runtime/livescore-runtime";
import type { RuntimeEntityState } from "../../../../shared/runtime/livescore-runtime";
type WrapperRequest = { endpoint?: string; query?: Record<string, unknown> };
type QuickFilter = "all" | "live" | "hot";
type Contract = { panel?: PanelContract; hotCompetitionIds?: string[]; pinStorageKey?: string; dateFilter?: DateFilterConfig; sortMode?: string; priorityCompetitionIds?: string[]; endpoint?: string; query?: Record<string, unknown>; pollInterval?: number; activeCompanyId?: string; oddsFormat?: string; liveUpcomingOddsRequest?: WrapperRequest };
type InitialState = { request?: Contract; data?: unknown };
type BoardState = { quickFilter: QuickFilter; contract: Contract; companyId: string; entities: RuntimeEntityState; viewIds: string[]; hiddenIds: Set<string>; pinnedIds: Set<string>; pinnedLeagues: Set<string>; panel?: ReturnType<typeof mountMatchPanel>; pinStorageKey: string; requestId: number; timer: number; clockTimer: number; controller: AbortController | null; lastSyncAt: number; unsubscribe: (() => void) | null };
const states = new WeakMap<HTMLElement, BoardState>();
const pendingPaints = new WeakMap<HTMLElement, { state: BoardState; prune: boolean }>();
const betTriggerAttributes = new Set(['data-gwd-bet-trigger', 'role', 'tabindex', 'aria-disabled', 'aria-haspopup', 'aria-label']);
const renderedRows = new WeakMap<Element, string>();
type VirtualView = { content: HTMLElement; list: ReturnType<typeof createOddsVirtualList>; render: (host: HTMLElement, start: number, end: number) => void };
const virtualViews = new WeakMap<HTMLElement, VirtualView>();
const STORAGE_KEY = "gwd_odds_board_hidden_matches";
const readHidden = (): Set<string> => { try { const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); return new Set(Array.isArray(value) ? value.map(String) : []); } catch { return new Set(); } };
const readPins = (key: string): Set<string> => { try { const ids = JSON.parse(localStorage.getItem(key) || '[]'); return new Set(Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string' && id.length > 0) : []); } catch { return new Set(); } };
const writePins = (state: BoardState) => { try { localStorage.setItem(state.pinStorageKey, JSON.stringify([...state.pinnedIds])); } catch {} };
const writeHidden = (ids: Set<string>) => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids])); } catch {} };
const object = (value: unknown): Record<string, any> => value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, any> : {};
const requestPage = async (contract: Contract, page: number, signal: AbortSignal): Promise<unknown> => {
  const endpoint = String(contract.endpoint || "");
  if (!endpoint) throw new Error("Missing Odds Board wrapper endpoint");
  return livescoreRuntime.fetchJson(endpoint, { ...(contract.dateFilter?.value ? { date: contract.dateFilter.value } : {}), ...(contract.sortMode === "league" ? { sort: "league" } : {}), ...(page === 1 ? {} : { page }) }, signal, "no-cache");
};
const wrapperContract = (contract: Contract, request?: WrapperRequest): Contract => ({
  ...contract,
  endpoint: String(request?.endpoint || ""),
  query: {},
});
const payloadData = (payload: unknown): Record<string, any> => {
  const root = object(payload); const data = object(root.data ?? root);
  return data;
};
const payloadMatches = (payload: unknown): any[] => {
  const data = payloadData(payload);
  const matches = Array.isArray(data.matches) ? data.matches : Array.isArray(data.items) ? data.items : [];
  return matches.map((match: any) => match?.id ? match : { ...match, id: match?.info?.id }).filter((match: any) => match.id);
};
const mergePayloadIntoState = (state: BoardState, payload: unknown, finalSnapshot = false, source = "odds-board") => {
  const data = payloadData(payload); const matches = payloadMatches(payload);
  const relation = { ...object(data.relation), teams: object(data.relation).teams || data.teams || [], competitions: object(data.relation).competitions || data.competitions || [] };
  const result = livescoreRuntime.mergeMatches(matches);
  const relationKeys = livescoreRuntime.mergeRelations(relation);
  if (finalSnapshot) state.viewIds = result.ids;
  else { const ids = new Set(state.viewIds); result.ids.forEach((id) => ids.add(id)); state.viewIds = [...ids]; }
  if (result.changedIds.length || result.addedIds.length) livescoreRuntime.emit("matches:update", { changedIds: result.changedIds, addedIds: result.addedIds, source });
  if (relationKeys.length) livescoreRuntime.emit("relations:update", { relationKeys, source });
  return result.ids;
};
// Publish validated pages immediately; prune the previous scope snapshot only on completion.
const requestPages = async (contract: Contract, signal: AbortSignal, onPage: (payload: unknown, ids: string[]) => void): Promise<string[]> => {
  const ids: string[] = []; const seen = new Set<string>();
  let total = 0; let pages = 1;
  for (let page = 1; page <= pages; page += 1) {
    const payload = object(await requestPage(contract, page, signal));
    if (signal.aborted) throw new DOMException('Date scope changed', 'AbortError');
    const data = payloadData(payload);
    const responseTotal = Number(data.total);
    if (payload.success === false || data.total == null || !Number.isSafeInteger(responseTotal) || responseTotal < 0 || !Array.isArray(data.matches)) {
      throw new Error('Invalid combined odds response');
    }
    if (page === 1) { total = responseTotal; pages = Math.max(1, Math.ceil(total / 50)); }
    if (total > 0 && !data.matches.length) throw new Error('Incomplete combined odds page');
    for (const match of data.matches) {
      const id = String(match?.id ?? match?.info?.id ?? '');
      if (!id) throw new Error('Missing combined odds match ID');
      if (!seen.has(id)) { seen.add(id); ids.push(id); }
    }
    onPage(payload, [...ids]);
  }
  if (ids.length < total) throw new Error('Incomplete combined odds snapshot');
  return ids;
};
const nodeKey = (node: Element): string => {
  if (node instanceof HTMLElement && node.dataset.gwdOddsMatchId) return `match:${node.dataset.gwdOddsMatchId}`;
  if (node instanceof HTMLElement && node.dataset.gwdOddsLeagueId) { const nextMatchId = node.nextElementSibling instanceof HTMLElement ? node.nextElementSibling.dataset.gwdOddsMatchId || "" : ""; return `league:${node.dataset.gwdOddsLeagueId}:${nextMatchId}`; }
  return node.classList.contains("gwd-odds-board__empty") ? "empty" : "";
};
const patchNode = (current: Node, incoming: Node): Node => {
  if (current.nodeType !== incoming.nodeType || (current instanceof Element && incoming instanceof Element && current.tagName !== incoming.tagName)) {
    current.parentNode?.replaceChild(incoming, current);
    return incoming;
  }
  if (current.nodeType === Node.TEXT_NODE) {
    if (current.nodeValue !== incoming.nodeValue) current.nodeValue = incoming.nodeValue;
    return current;
  }
  if (!(current instanceof Element) || !(incoming instanceof Element)) return current;
  if (current.hasAttribute('data-gwd-odds-panel') && incoming.hasAttribute('data-gwd-odds-panel')) return current;

  Array.from(current.attributes).forEach((attribute) => {
    // Popup accessibility is owned by prepareBetTriggers, not the incoming renderer.
    if (current.hasAttribute('data-gwd-bet-trigger') && betTriggerAttributes.has(attribute.name)) return;
    if (!incoming.hasAttribute(attribute.name)) current.removeAttribute(attribute.name);
  });
  Array.from(incoming.attributes).forEach((attribute) => {
    if (current.getAttribute(attribute.name) !== attribute.value) current.setAttribute(attribute.name, attribute.value);
  });

  const incomingChildren = Array.from(incoming.childNodes);
  incomingChildren.forEach((incomingChild, index) => {
    const currentChild = current.childNodes[index];
    if (!currentChild) current.appendChild(incomingChild);
    else patchNode(currentChild, incomingChild);
  });
  while (current.childNodes.length > incomingChildren.length) current.lastChild?.remove();
  return current;
};
const reconcileContent = (content: HTMLElement, html: string, prune = true) => {
  const template = document.createElement("template"); template.innerHTML = html;
  const nextNodes = Array.from(template.content.children);
  if (!prune && nextNodes.length === 1 && nextNodes[0].classList.contains("gwd-odds-board__empty")) return;
  const currentByKey = new Map<string, Element>();
  Array.from(content.children).forEach((node) => { const key = nodeKey(node); if (key) currentByKey.set(key, node); });
  const retained = new Set<Element>(); let reference: Element | null = content.firstElementChild;
  nextNodes.forEach((incoming) => {
    const key = nodeKey(incoming); const current = key ? currentByKey.get(key) : undefined;
    let node = current || incoming;
    const signature = incoming.outerHTML;
    if (current && renderedRows.get(current) !== signature) node = patchNode(current, incoming) as Element;
    if (!current || renderedRows.get(current) !== signature) prepareBetTriggers(node as HTMLElement);
    renderedRows.set(node, signature);
    if (node !== reference) content.insertBefore(node, reference);
    retained.add(node); reference = node.nextElementSibling;
  });
  if (prune) Array.from(content.children).forEach((node) => { if (!retained.has(node)) node.remove(); });
};
const paint = (root: HTMLElement, state: BoardState, prune = true) => {
  const pending = pendingPaints.get(root);
  if (pending) {
    pending.state = state;
    pending.prune = prune;
    return;
  }
  const request = { state, prune };
  pendingPaints.set(root, request);
  window.requestAnimationFrame(() => {
    pendingPaints.delete(root);
    if (!root.isConnected || states.get(root) !== request.state) return;
    paintNow(root, request.state, request.prune);
  });
};
const paintNow = (root: HTMLElement, state: BoardState, prune = true) => {
  const content = root.querySelector<HTMLElement>("[data-gwd-odds-content]"); const excludedIds = state.hiddenIds;
  const hotIds = new Set((state.contract.hotCompetitionIds || []).map(String));
  const renderableIds = state.viewIds.filter((id) => {
    const info = object(state.entities.matches[id]?.info);
    const statusId = Number(info.statusId);
    if (statusId < 1 || statusId > 7 || !Number.isFinite(statusId)) return false;
    if (state.quickFilter === 'live' && (statusId < 2 || statusId > 7)) return false;
    if (state.quickFilter === 'hot' && !hotIds.has(String(info.competitionId || ''))) return false;
    const home = state.entities.teams[String(info.homeTeamId ?? "")] || object(info.homeTeam);
    const away = state.entities.teams[String(info.awayTeamId ?? "")] || object(info.awayTeam);
    const label = (team: Record<string, any>) => String(team.shortName || "").trim() || String(team.name || "").trim();
    return Boolean(label(home) && label(away));
  });
  const matches = renderableIds.map((id) => state.entities.matches[id]).filter(Boolean);
  const data = { matches, teams: state.entities.teams, competitions: state.entities.competitions };
  const custom = root.hasAttribute('data-gwd-custom-layout');
  let target = content; let mobileMode = false;
  if (custom) {
    const desktop = root.querySelector<HTMLElement>('[data-gwd-list]');
    const mobile = root.querySelector<HTMLElement>('[data-gwd-mobile-list]');
    // getClientRects also detects display:none on an ancestor, including empty slots.
    mobileMode = Boolean(mobile?.getClientRects().length);
    target = mobileMode ? mobile : desktop;
    const inactive = mobileMode ? desktop : mobile;
    if (inactive?.childElementCount) {
      state.panel?.close(); inactive.replaceChildren();
    }
  }
  const ordered = orderedOddsMatches(data, state.companyId, excludedIds, state.pinnedIds, state.pinnedLeagues);
  const groupCounts = new Map<string, number>();
  let groupFirst = ''; let previousGroup = '';
  for (const match of ordered) {
    const id = String(match.id ?? match.info?.id ?? '');
    const league = String(match.info?.competitionId ?? '');
    const group = `${league}:${!state.pinnedLeagues.has(league) && state.pinnedIds.has(id)}`;
    if (group !== previousGroup || !groupFirst) { groupFirst = id; previousGroup = group; }
    groupCounts.set(groupFirst, (groupCounts.get(groupFirst) || 0) + 1);
  }
  const renderRange = (host: HTMLElement, start: number, end: number) => {
    const slice = { ...data, matches: ordered.slice(start, end) };
    const html = renderOddsBoard(slice, state.companyId, excludedIds, normalizeDisplayOddsFormat(state.contract.oddsFormat), state.pinnedIds, state.pinnedLeagues, ordered[start - 1]);
    reconcileContent(host, custom ? renderCustomOdds(root, html, slice, mobileMode, groupCounts) : html, prune);
  };
  let virtual = virtualViews.get(root);
  if (virtual && (virtual.content !== target || ordered.length <= 100)) {
    virtual.list.dispose(); virtual.content.replaceChildren(); virtualViews.delete(root); virtual = undefined;
    state.panel?.close();
  }
  if (target && ordered.length > 100) {
    if (!virtual) {
      const view = { content: target, render: renderRange } as VirtualView;
      view.list = createOddsVirtualList(target, (host, start, end) => view.render(host, start, end), () => { syncBetPopup(root); state.panel?.sync(); });
      virtualViews.set(root, view); virtual = view;
    }
    virtual.render = renderRange;
    virtual.list.update(ordered.map(match => String(match.id ?? match.info?.id ?? '')));
  } else if (target) renderRange(target, 0, ordered.length);
  if (state.controller) root.querySelectorAll<HTMLElement>('.gwd-odds-board__empty').forEach((empty) => {
    empty.textContent = 'Đang tải tỷ lệ kèo...';
  });
  const count = root.querySelector<HTMLElement>("[data-gwd-odds-hidden-count]"); if (count) count.textContent = String(state.hiddenIds.size);
  syncBetPopup(root);
  state.panel?.sync();
};
const setLoadingMore = (root: HTMLElement, visible: boolean) => {
  const loading = root.querySelector<HTMLElement>("[data-gwd-odds-loading-more]");
  if (loading) loading.hidden = !visible;
};
const updateClocks = (root: HTMLElement, state: BoardState) => root.querySelectorAll<HTMLElement>("[data-gwd-odds-match-id]").forEach((row) => {
  const matchId = String(row.dataset.gwdOddsMatchId || ""); const info = state.entities.matches[matchId]?.info;
  const clock = row.querySelector<HTMLElement>("[data-gwd-odds-clock]"); if (!clock || !info) return;
  const html = formatMatchClockHtml(info); if (clock.innerHTML !== html) clock.innerHTML = html;
});
const pollInterval = (state: BoardState): number => Math.max(3_000, Number(state.contract.pollInterval || 30_000));
const schedulePoll = (root: HTMLElement, state: BoardState) => {
  window.clearTimeout(state.timer);
  state.timer = window.setTimeout(() => void refresh(root, state), pollInterval(state));
};
const refresh = async (root: HTMLElement, state: BoardState, foreground = false, force = false) => {
  if (!force && state.controller) return;
  if (force) state.controller?.abort();
  const controller = new AbortController(); state.controller = controller; const requestId = ++state.requestId;
  const status = root.querySelector<HTMLElement>("[data-gwd-odds-status]"); root.classList.toggle("is-loading", foreground); if (status) status.textContent = foreground ? "Đang tải tỷ lệ kèo..." : "";
  try {
    setLoadingMore(root, true);
    const previousIds = [...state.viewIds];
    if (foreground) paint(root, state, true);
    const ids = await requestPages(wrapperContract(state.contract, state.contract.liveUpcomingOddsRequest), controller.signal, (payload, loadedIds) => {
      if (state.requestId !== requestId || controller.signal.aborted) return;
      const loaded = new Set(loadedIds);
      livescoreRuntime.batch(() => {
        mergePayloadIntoState(state, payload, false, 'odds-board-combined');
        state.viewIds = [...loadedIds, ...previousIds.filter((id) => !loaded.has(id))];
      });
      paint(root, state, true);
      if (status) status.textContent = 'Đang tải thêm tỷ lệ kèo...';
    });
    if (state.requestId !== requestId || controller.signal.aborted) return;
    state.viewIds = ids;
    state.lastSyncAt = Date.now();
    if (status) status.textContent = '';
  }
  catch (error) { if (state.requestId !== requestId || (error instanceof DOMException && error.name === "AbortError")) return; if (status) status.textContent = navigator.onLine ? (state.viewIds.length ? "Chưa tải đủ tỷ lệ kèo. Đang giữ phần đã tải; sẽ tự thử lại." : "Không tải được tỷ lệ kèo.") : "Đang ngoại tuyến."; console.warn("GWD odds board request failed", error); }
  finally { if (state.requestId === requestId) { state.controller = null; paint(root, state, true); setLoadingMore(root, false); root.classList.remove("is-loading"); schedulePoll(root, state); } }
};
export const mountOddsBoard = (root: HTMLElement) => {
  if (states.has(root)) return;
  const rawState = root.dataset.gwdState;
  if (!rawState) return;
  let initialState: InitialState;
  try { initialState = JSON.parse(rawState) as InitialState; } catch (error) { console.warn("GWD odds board initial state is invalid", error); return; }
  delete root.dataset.gwdState;
  const contract = initialState.request || {};
  const pinStorageKey = contract.pinStorageKey || `gwd_odds_pins:${window.location.origin}`;
  const state: BoardState = { quickFilter: "all", pinStorageKey, pinnedIds: readPins(pinStorageKey), pinnedLeagues: readPins(pinStorageKey + ':leagues'), contract, companyId: String(contract.activeCompanyId || "14"), entities: livescoreRuntime.entities, viewIds: [], hiddenIds: readHidden(), requestId: 0, timer: 0, clockTimer: 0, controller: null, lastSyncAt: Date.now(), unsubscribe: null };
  states.set(root, state); mergePayloadIntoState(state, initialState.data || {}, true, "odds-board-initial");
  let width = root.getBoundingClientRect().width;
  const layoutObserver = new ResizeObserver(() => {
    const next = root.getBoundingClientRect().width;
    if (next !== width) { width = next; paint(root, state); }
  });
  layoutObserver.observe(root);
  window.addEventListener('resize', () => {
    if (!root.isConnected) { layoutObserver.disconnect(); virtualViews.get(root)?.list.dispose(); return; }
    paint(root, state);
  });
  root.addEventListener('gwd:odds-panel-change', () => virtualViews.get(root)?.list.schedule());
  mountBetPopup(root);
  state.panel = mountMatchPanel(root, contract.panel || {}, () => ({ company: state.companyId, format: String(state.contract.oddsFormat || 'decimal'), matches: state.entities.matches }));
  const unsubscribeMatches = livescoreRuntime.subscribe("matches:update", ({ changedIds = [], addedIds = [] }) => {
    const visibleIds = new Set(state.viewIds); if ([...changedIds, ...addedIds].some((id) => visibleIds.has(id))) paint(root, state, true);
  });
  const unsubscribeRelations = livescoreRuntime.subscribe("relations:update", () => paint(root, state, true));
  state.unsubscribe = () => { unsubscribeMatches(); unsubscribeRelations(); };
  paint(root, state);
  const chooseCompany = (companyId: string) => {
    const choices = root.querySelectorAll<HTMLElement>('[data-gwd-odds-company-id], [data-gwd-odds-company-select] option');
    if (!Array.from(choices).some((choice) => (choice instanceof HTMLOptionElement ? choice.value : choice.dataset.gwdOddsCompanyId) === companyId)) return;
    state.companyId = companyId;
    root.querySelectorAll<HTMLElement>('[data-gwd-odds-company-id]').forEach((button) => {
      button.classList.toggle('is-active', button.dataset.gwdOddsCompanyId === companyId);
    });
    root.querySelectorAll<HTMLSelectElement>('[data-gwd-odds-company-select]').forEach((select) => { select.value = companyId; });
    paint(root, state);
  };
  root.addEventListener('change', (event) => {
    const select = event.target;
    if (select instanceof HTMLSelectElement && select.matches('[data-gwd-odds-format-select]')) {
      state.contract.oddsFormat = normalizeDisplayOddsFormat(select.value);
      root.querySelectorAll<HTMLSelectElement>('[data-gwd-odds-format-select]').forEach((control) => { control.value = state.contract.oddsFormat!; });
      paint(root, state);
    }
    if (select instanceof HTMLSelectElement && select.matches('[data-gwd-odds-company-select]')) chooseCompany(select.value);
  });
  root.addEventListener("click", (event) => { const target = event.target instanceof Element ? event.target : null; const company = target?.closest<HTMLElement>("[data-gwd-odds-company-id]"); if (company?.dataset.gwdOddsCompanyId) { chooseCompany(company.dataset.gwdOddsCompanyId); return; } if (target?.closest("[data-gwd-odds-show-all]")) { state.hiddenIds.clear(); writeHidden(state.hiddenIds); paint(root, state); return; } const leaguePin = target?.closest<HTMLElement>('[data-gwd-odds-league-pin]');
    if (leaguePin) {
      const id = leaguePin.closest<HTMLElement>('[data-gwd-odds-league-id]')?.dataset.gwdOddsLeagueId;
      if (id) {
        if (state.pinnedLeagues.has(id)) state.pinnedLeagues.delete(id); else state.pinnedLeagues.add(id);
        try { localStorage.setItem(state.pinStorageKey + ':leagues', JSON.stringify([...state.pinnedLeagues])); } catch {}
        paint(root, state);
      }
      return;
    }
    const pin = target?.closest<HTMLElement>('[data-gwd-odds-pin]');
    if (pin) {
      const id = pin.closest<HTMLElement>('[data-gwd-odds-match-id]')?.dataset.gwdOddsMatchId;
      if (id) { if (state.pinnedIds.has(id)) state.pinnedIds.delete(id); else state.pinnedIds.add(id); writePins(state); paint(root, state); }
      return;
    }
    const hide = target?.closest<HTMLElement>("[data-gwd-odds-hide]"); const match = hide?.closest<HTMLElement>("[data-gwd-odds-match-id]"); if (match?.dataset.gwdOddsMatchId) { state.hiddenIds.add(match.dataset.gwdOddsMatchId); writeHidden(state.hiddenIds); paint(root, state); } });
  // A browser-tab restore commonly emits focus and pageshow together. Do not
  // abort/restart the first request for every one of them.
  const wake = () => {
    if (state.controller) return;
    window.clearTimeout(state.timer);
    void refresh(root, state);
  };
  window.addEventListener("focus", wake);
  window.addEventListener("online", () => {
    window.clearTimeout(state.timer);
    void refresh(root, state, false, true);
  });
  window.addEventListener("offline", () => { const status = root.querySelector<HTMLElement>("[data-gwd-odds-status]"); if (status) status.textContent = "Đang ngoại tuyến."; });
  window.addEventListener("pageshow", wake);
  if (!root.hasAttribute('data-gwd-custom-layout')) {
    const bookmakers = root.querySelector<HTMLElement>('[data-gwd-odds-bookmakers]');
    if (bookmakers) {
      const toolbar = document.createElement('div'); toolbar.className = 'gwd-odds-board__toolbar';
      const dateSlot = document.createElement('div'); dateSlot.dataset.gwdDateFilter = 'navigation';
      bookmakers.before(toolbar);
      const filters = root.querySelector<HTMLElement>('[data-gwd-odds-filters]');
      if (filters) toolbar.append(filters);
      toolbar.append(bookmakers, dateSlot);
    }
  }
  let filterSlot = root.querySelector<HTMLElement>('[data-gwd-odds-filters]');
  if (!filterSlot) {
    filterSlot = document.createElement('div'); filterSlot.dataset.gwdOddsFilters = '';
    root.prepend(filterSlot);
  }
  filterSlot.classList.add('gwd-odds-board__filters');
  filterSlot.setAttribute('role', 'group'); filterSlot.setAttribute('aria-label', 'Lọc trận đấu');
  const syncFilters = () => filterSlot!.querySelectorAll<HTMLButtonElement>('[data-gwd-odds-filter]').forEach((button) => {
    const active = button.dataset.gwdOddsFilter === state.quickFilter;
    button.setAttribute('aria-pressed', String(active)); button.classList.toggle('is-active', active);
  });
  filterSlot.replaceChildren(...([['all', 'Tất cả'], ['live', 'Live'], ['hot', 'Hot']] as const).map(([value, label]) => {
    const button = document.createElement('button'); button.type = 'button';
    button.dataset.gwdOddsFilter = value; button.textContent = label; return button;
  }));
  syncFilters();
  let selectingQuickFilter = false;
  const dateControl = mountDateFilter(root, contract.dateFilter || {}, (date) => {
    if (!selectingQuickFilter) { state.quickFilter = 'all'; syncFilters(); }
    state.controller?.abort();
    state.requestId += 1;
    state.contract = { ...state.contract, dateFilter: { ...state.contract.dateFilter, value: date } };
    state.viewIds = [];
    window.clearTimeout(state.timer);
    void refresh(root, state, true, true);
  }, () => {
    if (!selectingQuickFilter && state.quickFilter !== 'all') {
      state.quickFilter = 'all'; syncFilters(); paint(root, state, true);
    }
  });
  filterSlot.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest<HTMLButtonElement>('[data-gwd-odds-filter]') : null;
    const value = button?.dataset.gwdOddsFilter;
    if (value !== 'all' && value !== 'live' && value !== 'hot') return;
    state.quickFilter = value;
    selectingQuickFilter = true;
    if (value !== 'all') dateControl.select('');
    selectingQuickFilter = false;
    syncFilters(); paint(root, state, true);
  });
  void refresh(root, state);
  state.clockTimer = window.setInterval(() => updateClocks(root, state), 15_000);
};
export const oddsBoardDefaultLayout: LayoutRuntime = { feature: "odds-board", layout: "default", rootClass: "gwd-livescore-layout--default", mount: mountOddsBoard };
